/** @format */
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { createClassNames } from '@mpb-com/feather/helpers/styles';
import { Trans, useTranslation } from 'next-i18next';
import React, { FunctionComponent, useContext, useMemo } from 'react';
import styled from 'styled-components';

import ExtraAccessoryIcon from '@toucan/client/global/icons/frequency-range.svg';

import { MarketContext } from '@toucan/client/global/market/context';

import { getFilterOption } from '@toucan/client/features/search/components/product-search-results/components/accessory-filters/helpers';
import { getModelIdFromQuery } from '@toucan/client/features/search/helpers';
import { usePriceModifierGroups } from '@toucan/client/features/search/hooks/use-model-price-modifier-groups';

import { AccessoryOption } from '../accessory-option';
import { TEST_ID as dataTestId } from './constants';
import { styles } from './extra-accessory-filter.styles';
import { ExtraAccessoryFilterProps } from './types';

const StyledExtraAccessoryFilter = styled('div')`
    ${styles}
`;

export const ExtraAccessoryFilter: FunctionComponent<
    ExtraAccessoryFilterProps
> = ({ filter, onAccessorySelect, showIcon = true }) => {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);

    const { selectedOptions } = filter;

    const modelId = getModelIdFromQuery(filter.baseFilterQuery);

    const { data: modifierGroups } = usePriceModifierGroups(
        modelId,
        activeMarket.code
    );
    const extraModifierGroups = modifierGroups?.extra;
    const availableOptions = filter.options.map(({ value }) => Number(value));

    const extraAccessories = useMemo(() => {
        if (!extraModifierGroups) {
            return [];
        }

        const allModifiers = extraModifierGroups.flatMap((group) =>
            group.modifiers.filter((modifier) =>
                availableOptions.includes(modifier.id)
            )
        );

        return allModifiers;
    }, [extraModifierGroups, availableOptions]);

    const renderedFilterOptions = useMemo(
        () =>
            extraAccessories.map((modifier) => (
                <AccessoryOption
                    key={modifier.id}
                    option={getFilterOption(modifier.id, filter)}
                    selectedOptions={selectedOptions}
                    onAccessorySelect={onAccessorySelect}
                />
            )),
        [extraAccessories, filter, onAccessorySelect, selectedOptions]
    );

    return (
        <StyledExtraAccessoryFilter data-testid={dataTestId}>
            <div className="extra-accessory__title">
                <Icon
                    variant={IconVariant.CORE}
                    size={IconSize.XL}
                    data-testid={`${dataTestId}__icon`}
                    className={createClassNames('extra-accessory__icon', {
                        'extra-accessory__icon--hidden': !showIcon,
                    })}
                >
                    <ExtraAccessoryIcon />
                </Icon>
                <p className="extra-accessory__label">
                    {t('Extra accessories')}
                </p>
            </div>
            <p className="extra-accessory__description">
                <Trans components={{ ['bold-text']: <strong /> }}>
                    {t(
                        'Some items come with <bold-text>additional accessories</bold-text>. Choose any must-haves to narrow your search.'
                    )}
                </Trans>
            </p>
            <div className="extra-accessory__options">
                {renderedFilterOptions}
            </div>
        </StyledExtraAccessoryFilter>
    );
};
